import { format } from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon } from "lucide-react";
import React from "react";

import { Badge, Button, Card, CardContent } from "@/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useI18n } from "@/context";
import { usePermissions } from "@/context/permissions";

import { TSale, TSaleStatus } from "../../reports.types";

interface IReportsTableProps {
  sales: TSale[];
  hasProductBreakdown: boolean;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onOpenDetails: (sale: TSale) => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
}

export const ReportsTable: React.FC<IReportsTableProps> = ({
  sales,
  hasProductBreakdown,
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onOpenDetails,
  isLoading,
  isFetching,
  isError,
  error,
  isSuccess,
}) => {
  const { t } = useI18n();
  const { hasPermission } = usePermissions();

  // Verificar se o usuário tem permissão para visualizar detalhes
  // Se já está na página de reports, provavelmente tem reports:view
  // Mas vamos verificar para ser consistente
  const hasDetailsPermission = hasPermission("reports:view");

  const getBadgeVariant = (status: TSaleStatus) => {
    switch (status) {
      case TSaleStatus.approved:
        return "bg-green-500 text-white hover:bg-green-600";
      case TSaleStatus.pending:
        return "bg-yellow-500 text-white hover:bg-yellow-600";
      case TSaleStatus.failed:
        return "bg-red-500 text-white hover:bg-red-600";
      case TSaleStatus.refunded:
        return "bg-orange-500 text-white hover:bg-orange-600";
      case TSaleStatus.rejected:
        return "bg-purple-500 text-white hover:bg-purple-600";
      case TSaleStatus.cancelled:
        return "bg-gray-500 text-white hover:bg-gray-600";
      default:
        return "";
    }
  };

  const formatCurrency = (value: number) => {
    // Converte de centavos para reais
    const valueInReais = value / 100;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valueInReais);
  };

  const errorMessage =
    error instanceof Error
      ? error.message
      : error != null
        ? String(error)
        : "";

  if (isLoading) {
    return (
      <Card className="p-4">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <p className="text-muted-foreground">{t("generic.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="p-4">
        <CardContent className="flex flex-col items-center justify-center py-12 space-y-2">
          <p className="text-destructive font-medium">{t("generic.error")}</p>
          <p className="text-sm text-muted-foreground text-center max-w-lg">
            {errorMessage}
          </p>
        </CardContent>
      </Card>
    );
  }

  const showEmpty = isSuccess && sales.length === 0;

  return (
    <Card className="p-4">
      <CardContent>
        {isFetching && !isLoading && (
          <p className="text-xs text-muted-foreground mb-3">{t("generic.loading")}</p>
        )}
        {showEmpty ? (
          <div className="flex flex-col items-center justify-center p-8">
            <p className="text-muted-foreground">{t("reports.noSales")}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("reports.table.saleId")}</TableHead>
                <TableHead>{t("reports.table.planName")}</TableHead>
                {hasProductBreakdown && (
                  <>
                    <TableHead>{t("reports.table.productName")}</TableHead>
                    <TableHead>{t("reports.table.productValue")}</TableHead>
                  </>
                )}
                <TableHead>{t("reports.table.partnerName")}</TableHead>
                <TableHead>{t("reports.table.clientName")}</TableHead>
                <TableHead>{t("reports.table.clientCpf")}</TableHead>
                <TableHead>{t("reports.table.salePrice")}</TableHead>
                <TableHead>{t("reports.table.status")}</TableHead>
                <TableHead>{t("reports.table.saleDate")}</TableHead>
                {hasDetailsPermission && (
                  <TableHead>{t("reports.table.details")}</TableHead>
                )}
              </TableRow>
            </TableHeader>

            <TableBody>
              {sales.map((sale, i) => (
                <TableRow
                  key={
                    sale.product_id
                      ? `${sale.id}-${sale.product_id}`
                      : sale.id || String(i)
                  }
                >
                  <TableCell className="font-medium">{sale.id}</TableCell>
                  <TableCell>{sale.plan_name}</TableCell>
                  {hasProductBreakdown && (
                    <>
                      <TableCell>{sale.product_name ?? "—"}</TableCell>
                      <TableCell className="font-semibold">
                        {sale.product_value != null
                          ? formatCurrency(sale.product_value)
                          : "—"}
                      </TableCell>
                    </>
                  )}
                  <TableCell>{sale.partner_name}</TableCell>
                  <TableCell>{sale.client_name}</TableCell>
                  <TableCell>{sale.client_cpf}</TableCell>
                  <TableCell className="font-semibold">
                    {formatCurrency(sale.sale_price)}
                  </TableCell>
                  <TableCell>
                    <Badge className={getBadgeVariant(sale.status)}>
                      {t(`reports.status.${sale.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {format(new Date(sale.sale_date), "dd/MM/yyyy")}
                  </TableCell>
                  {hasDetailsPermission && (
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onOpenDetails(sale)}
                      >
                        <EyeIcon className="h-4 w-4 mr-2" />
                        {t("reports.table.viewDetails")}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Paginação */}
        {sales.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {t("reports.pagination.showing")}{" "}
              <span className="font-medium">
                {(currentPage - 1) * itemsPerPage + 1}
              </span>{" "}
              {t("reports.pagination.to")}{" "}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, totalItems)}
              </span>{" "}
              {t("reports.pagination.of")}{" "}
              <span className="font-medium">{totalItems}</span>{" "}
              {t("reports.pagination.results")}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
                {t("reports.pagination.previous")}
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    // Mostra primeira página, última página, página atual e páginas adjacentes
                    return (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    );
                  })
                  .map((page, index, array) => {
                    // Adiciona "..." entre páginas não consecutivas
                    const showEllipsis =
                      index > 0 && page - array[index - 1] > 1;

                    return (
                      <React.Fragment key={page}>
                        {showEllipsis && (
                          <span className="px-2 text-muted-foreground">
                            ...
                          </span>
                        )}
                        <Button
                          variant={
                            currentPage === page ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => onPageChange(page)}
                          className="min-w-[2.5rem]"
                        >
                          {page}
                        </Button>
                      </React.Fragment>
                    );
                  })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                {t("reports.pagination.next")}
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

